import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';
import prodLogo from 'assets/images/hospitalprivado.png';
import logo from 'assets/images/managemed-logo-2.png';
import { roundTo2 } from 'utils/roundTo2';
import { format, isValid } from 'date-fns';

const styles = StyleSheet.create({
  name: {
    marginLeft: 40,
    fontSize: 24,
  },
  medRecordTitle: {
    fontSize: 16,
    marginLeft: 40,
    marginTop: 20,
    marginBottom: 10,
  },
  logo: {
    width: 200,
    marginLeft: 40,
    marginTop: 40,
    marginBottom: 40,
  },
  tableRow: {
    borderBottomWidth: 1,
    borderBottomColor: 'grey',
    display: 'flex',
    flexDirection: 'row',
  },
  tableCell: {
    borderRightColor: 'grey',
    borderRightWidth: 1,
    minHeight: 25,
    width: 83.33,
    padding: 5,
  },
  tableCellText: {
    fontSize: 12,
  },
  table: {
    borderTopColor: 'grey',
    borderLeftColor: 'grey',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    marginLeft: 40,
    width: 500,
  },
  emptyState: {
    marginLeft: 'auto',
    marginRight: 'auto',
    fontSize: 12,
  },

  total: {
    fontSize: 18,
    marginTop: 15,
    marginLeft: 40,
    fontWeight: 500,
  },

  paymentsTable: {
    width: 125,
  },
  paymentsLabel: {
    marginTop: 25,
    marginBottom: 15,
  },
});

const columns = [
  { name: 'codigo', translationKey: 'Codigo' },
  { name: 'descripcion', translationKey: 'Descripcion' },
  { name: '', translationKey: 'Precio', valueGetter: ({ precio }) => new Intl.NumberFormat().format(+precio) },
  { name: '', translationKey: 'Cantidad', valueGetter: ({ amount }) => new Intl.NumberFormat().format(+amount) },
  { name: '', translationKey: 'Total parcial', valueGetter: ({ amount, precio }) => new Intl.NumberFormat().format(roundTo2(amount * precio)) },
  { name: '', translationKey: 'Esta resuelto', valueGetter: ({ resolved }) => (resolved ? 'Si' : 'No') },
];

const paymentColumns = [
  { name: '', translationKey: 'Fecha', valueGetter: ({ createdAt }) => format(createdAt, 'dd.MM.yyyy') },
  { name: 'paymentNumber', translationKey: 'No. Boleta' },
  { name: '', translationKey: 'Monto', valueGetter: ({ amount }) => roundTo2(amount) },
  { name: '', translationKey: 'Registrado Por', valueGetter: ({ createdBy }) => `${createdBy?.firstName} ${createdBy?.lastName}` },
];

const SkuTable = ({ catalogList }: any) => {
  return (
    <View style={catalogList.length ? styles.table : {}}>
      {catalogList.length ? (
        <View style={styles.tableRow}>
          {columns.map((field) => (
            <View style={styles.tableCell} key={field.name}>
              <Text style={styles.tableCellText}>{field.translationKey}</Text>
            </View>
          ))}
        </View>
      ) : (
        <View style={styles.emptyState}>
          <Text>No se han agregado skus</Text>
        </View>
      )}

      {catalogList.map((row) => (
        <View style={styles.tableRow} key={row._id}>
          {columns.map(({ name, valueGetter }) => (
            <View style={styles.tableCell} key={name}>
              <Text style={styles.tableCellText}>{valueGetter ? valueGetter(name ? row[name] : row) : row[name]}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};

const PaymentsTable = ({ payments }: any) => {
  return (
    <View style={payments.length ? styles.table : {}}>
      <View style={styles.tableRow}>
        {paymentColumns.map((field) => (
          <View style={[styles.tableCell, styles.paymentsTable]} key={field.name}>
            <Text style={styles.tableCellText}>{field.translationKey}</Text>
          </View>
        ))}
      </View>

      {payments?.map((row) => (
        <View style={styles.tableRow} key={row._id}>
          {paymentColumns.map(({ name, valueGetter }) => (
            <View style={[styles.tableCell, styles.paymentsTable]} key={name}>
              <Text style={styles.tableCellText}>{valueGetter ? valueGetter(name ? row[name] : row) : row[name]}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};

const MedicalRecordItem = ({ medicalRecord }: any) => {
  return (
    <View>
      <Text style={styles.medRecordTitle}>
        Record Medico: {isValid(new Date(medicalRecord.name)) ? format(new Date(medicalRecord.name), 'dd.MM.yyyy') : medicalRecord.name}
      </Text>
      <SkuTable catalogList={medicalRecord.catalog} />
    </View>
  );
};

export const BillingPdf = ({ caseItem, patient }: any) => {
  console.log(caseItem);
  return (
    <Document>
      <Page size="A4">
        <Image style={styles.logo} src={process.env.REACT_APP_ENV === 'master' ? prodLogo : logo} />

        <View>
          <Text style={styles.name}>
            Paciente: {patient.firstName} {patient.lastName}
          </Text>
        </View>
        <View>
          <Text style={styles.name}>Caso: {isValid(new Date(caseItem.name)) ? format(new Date(caseItem.name), 'dd.MM.yyyy') : caseItem.name}</Text>
        </View>
        {caseItem.medicalRecords.length ? (
          caseItem.medicalRecords?.map((mr) => <MedicalRecordItem key={mr._id} medicalRecord={mr} />)
        ) : (
          <View style={styles.emptyState}>
            <Text>No se han agregado registros médicos</Text>
          </View>
        )}

        <Text style={[styles.name, styles.paymentsLabel]}>Pagos</Text>
        {caseItem?.payments?.length ? (
          <PaymentsTable payments={caseItem?.payments} />
        ) : (
          <View style={styles.emptyState}>
            <Text>No se han agregado pagos</Text>
          </View>
        )}

        <Text style={styles.total}>TOTAL DE CUENTA: {new Intl.NumberFormat().format(caseItem.total)}</Text>
        <Text style={styles.total}>PAGO TOTAL: {roundTo2(caseItem.paidTotal)}</Text>
        <Text style={styles.total}>TOTAL DE CUENTA PENDIENTE: {roundTo2(caseItem.total - caseItem.paidTotal)}</Text>
      </Page>
    </Document>
  );
};
